import { useState, useRef, useEffect } from 'react';

/**
 * PhoneInput — usar siempre con Controller de React Hook Form.
 * Nunca con register().
 * Guarda el valor como "+54 3515726588" (código + espacio + número).
 */

const COUNTRIES = [
  { code: '+54',  flag: '🇦🇷', name: 'Argentina' },
  { code: '+598', flag: '🇺🇾', name: 'Uruguay' },
  { code: '+591', flag: '🇧🇴', name: 'Bolivia' },
  { code: '+56',  flag: '🇨🇱', name: 'Chile' },
  { code: '+595', flag: '🇵🇾', name: 'Paraguay' },
  { code: '+55',  flag: '🇧🇷', name: 'Brasil' },
  { code: '+57',  flag: '🇨🇴', name: 'Colombia' },
  { code: '+51',  flag: '🇵🇪', name: 'Perú' },
  { code: '+52',  flag: '🇲🇽', name: 'México' },
  { code: '+34',  flag: '🇪🇸', name: 'España' },
  { code: '+1',   flag: '🇺🇸', name: 'USA / Canadá' },
];

function parseValue(value) {
  if (!value) return { code: '+54', number: '' };
  const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
  for (const country of sorted) {
    if (value.startsWith(country.code + ' ')) {
      return { code: country.code, number: value.slice(country.code.length + 1) };
    }
    if (value.startsWith(country.code)) {
      return { code: country.code, number: value.slice(country.code.length) };
    }
  }
  return { code: '+54', number: value };
}

export function PhoneInput({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { code, number } = parseValue(value ?? '');
  const selected = COUNTRIES.find((c) => c.code === code) ?? COUNTRIES[0];

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function handleCodeChange(newCode) {
    setOpen(false);
    onChange(newCode + ' ' + number);
  }

  function handleNumberChange(e) {
    onChange(code + ' ' + e.target.value);
  }

  return (
    <div ref={ref} className="flex">
      {/* Selector de país */}
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-full flex items-center gap-1.5 px-3 border border-r-0 border-gray-200 rounded-l-md bg-white text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C] focus:z-10 whitespace-nowrap"
        >
          <span>{selected.flag}</span>
          <span className="text-[#6B7280]">{selected.code}</span>
          <span className="text-[#6B7280] text-[10px]">▾</span>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 left-0 w-52 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
            {COUNTRIES.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => handleCodeChange(c.code)}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-['Inter'] hover:bg-gray-50"
              >
                <span>{c.flag}</span>
                <span className="text-[#0D2B5C]">{c.name}</span>
                <span className="ml-auto text-[#6B7280]">{c.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Número */}
      <input
        type="tel"
        value={number}
        onChange={handleNumberChange}
        className="flex-1 min-w-0 border border-gray-200 rounded-r-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C]"
        placeholder="3515726588"
      />
    </div>
  );
}
